import { ArrowUp } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Wordmark } from './Logo';
import { useLang, LANGS } from '../i18n';

const LanguageSwitcher = () => {
    const { lang, setLang } = useLang();
    return (
        <div className="flex flex-col gap-2" data-testid="footer-lang-switcher">
            <span className="font-ink text-[9px] uppercase tracking-[0.3em] text-pastelcream/40">
                {useLangLabel(lang)}
            </span>
            <div className="flex items-center gap-1 rounded-full border border-pastelcream/25 p-1">
                {LANGS.map((l) => (
                    <button
                        key={l.code}
                        onClick={() => setLang(l.code)}
                        className={`w-11 py-1.5 rounded-full text-[11px] font-bold tracking-[0.1em] transition-colors ${lang === l.code ? 'bg-crimson text-pastelcream' : 'text-pastelcream/60 hover:text-pastelcream'}`}
                        data-testid={`lang-${l.code}`}
                        aria-label={`Cambiar idioma a ${l.label}`}
                    >
                        {l.label}
                    </button>
                ))}
            </div>
        </div>
    );
};

const useLangLabel = (lang) => ({ es: 'Idioma', en: 'Language', fr: 'Langue', pt: 'Idioma' }[lang] || 'Idioma');

export default function Footer() {
    const year = new Date().getFullYear();
    const navigate = useNavigate();
    const { t } = useLang();

    const go = (item) => {
        if (item.route) {
            navigate(item.route);
            return;
        }
        const el = document.querySelector(item.id);
        if (el) {
            if (window.__lenis) window.__lenis.scrollTo(el, { offset: -70, duration: 1.4 });
            else el.scrollIntoView({ behavior: 'smooth' });
        } else {
            navigate('/', { state: { scrollTo: item.id } });
        }
    };

    const items = [
        ['#historia', t('nav_historia')],
        ['#mision', t('nav_mision')],
        ['#vision', t('nav_vision')],
        ['/servicios', t('nav_servicios')],
        ['#casos', t('nav_casos')],
        ['#arquitectura', t('nav_arquitectura')],
    ];

    return (
        <footer className="bg-ink text-pastelcream pt-20 pb-10" data-testid="site-footer">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <div className="flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10 pb-14 border-b border-pastelcream/15">
                    <div data-testid="footer-logo-space">
                        <div className="flex items-center gap-4">
                            <span className="w-16 h-16 rounded-2xl bg-pastelcream flex items-center justify-center shadow-lg" data-testid="footer-logo-slot">
                                <img src="/pt-logo.png" alt="Logo Pt de #PICHONTEAM" className="w-11 h-auto" data-testid="footer-pt-logo" />
                            </span>
                            <div>
                                <div className="font-wordmark text-3xl sm:text-4xl font-semibold tracking-[0.06em]">#PICHONTEAM</div>
                                <div className="font-wordmark text-[11px] tracking-[0.4em] text-crimson mt-2">GROWTH PARTNERS</div>
                            </div>
                        </div>
                        <p className="font-display italic text-pastelcream/70 mt-5">{t('footer_tagline')}</p>
                    </div>

                    <nav className="grid grid-cols-2 gap-x-14 gap-y-3" data-testid="footer-nav">
                        {items.map(([id, label]) => (
                            <button
                                key={id}
                                onClick={() => go(id.startsWith('/') ? { route: id } : { id })}
                                className="ink-link text-left text-sm text-pastelcream/70 hover:text-pastelcream transition-colors"
                                data-testid={`footer-link-${label.toLowerCase().replace(/\s+/g, '-').normalize('NFD').replace(/[\u0300-\u036f]/g, '')}`}
                            >
                                {label}
                            </button>
                        ))}
                    </nav>

                    <div className="flex flex-col items-start lg:items-end gap-6">
                        <LanguageSwitcher />
                        <button
                            onClick={() => navigate('/')}
                            className="inline-flex items-center gap-2 rounded-full border border-pastelcream/25 px-5 py-3 text-[11px] font-bold uppercase tracking-[0.16em] hover:border-crimson hover:text-crimson transition-colors"
                            data-testid="footer-back-to-top"
                        >
                            <ArrowUp size={14} />
                            {t('footer_volver')}
                        </button>
                    </div>
                </div>

                <div className="pt-8 flex flex-col sm:flex-row items-center justify-between gap-4">
                    <p className="font-ink text-[10px] uppercase tracking-[0.2em] text-pastelcream/40" data-testid="footer-legal">
                        © {year} #PICHONTEAM Growth Partners - {t('footer_legal')}
                    </p>
                    <p className="font-ink text-[10px] uppercase tracking-[0.2em] text-pastelcream/40">
                        {t('footer_services_line')}
                    </p>
                </div>
            </div>
        </footer>
    );
}
